import { cva } from "class-variance-authority";

export { default as Tab } from "./Tab.vue";
export { default as Tabs } from "./Tabs.vue";
export { default as TabsWithSearch } from "./TabsWithSearch.vue";

export const orientationVariants = ["horizontal", "vertical"];
export const sizes = ["sm", "lg", "xl", "custom"];
export const tabVariants = [
    "button-primary",
    "button-gray",
    "button-white",
    "underline",
    "underline-filled",
    "button-white-border",
    "details-nav",
];

export const variants = cva(["flex"], {
    variants: {
        orientation: {
            horizontal: ["flex-col"],
            vertical: ["[&>div:first-of-type]:flex-col"],
        },
        variant: {
            notifications: [
                "grow",
                "md:rounded-b-xl",
                "md:[&>div:first-of-type]:pr-4",
                "md:[&>div:first-of-type]:pb-3",
                "md:[&>div:first-of-type]:pl-3",
                "overflow-y-hidden",
            ],
        },
    },
    compoundVariants: [
        {
            orientation: "vertical",
            fullWidth: true,
            class: ["[&>div:first-of-type]:w-full"],
        },
    ],
    defaultVariants: {
        orientation: "horizontal",
        variant: "default",
    },
});

export const containerVariants = cva(
    ["flex", "overflow-x-auto", "no-scrollbar"],
    {
        variants: {
            orientation: {
                vertical: ["flex-col"],
            },
            variant: {
                taskDetails: [
                    "h-[55px]",
                    "pt-3",
                    "px-4",
                    "group-data-[collapsed=false]/tabs:border-transparent",
                    "group-data-[collapsed=true]/tabs:h-full",
                    "group-data-[collapsed=true]/tabs:p-0",
                    "group-data-[collapsed=true]/tabs:gap-2",
                ],
            },
            tabVariant: {
                "button-white-border": [
                    "bg-secondary_alt",
                    "border",
                    "border-secondary",
                    "p-1.5",
                    "rounded-xl",
                ],
            },
        },
        compoundVariants: [
            {
                orientation: "horizontal",
                fullWidth: true,
                class: ["grow", "items-center", "shrink-0"],
            },
            {
                orientation: "vertical",
                fullWidth: true,
                class: ["w-full"],
            },
            {
                tabVariant: [
                    "button-primary",
                    "button-gray",
                    "button-white",
                    "button-white-border",
                ],
                class: ["gap-1"],
            },
            {
                tabVariant: [
                    "button-primary",
                    "button-gray",
                    "button-white",
                    "button-white-border",
                ],
                size: "xl",
                class: ["gap-2"],
            },
            {
                tabVariant: ["underline", "underline-filled"],
                class: ["gap-3", "border-secondary"],
            },
            {
                tabVariant: ["underline", "underline-filled"],
                variant: "linkedToSelector",
                class: ["border-transparent", "pb-px md:pb-0"],
            },
            {
                tabVariant: ["underline", "underline-filled"],
                orientation: "horizontal",
                class: ["border-b"],
            },
        ],
        defaultVariants: {
            orientation: "horizontal",
            variant: "default",
            tabVariant: "button-primary",
            fullWidth: false,
        },
    }
);

export const tabButtonVariants = cva(
    [
        "flex",
        "items-center",
        "rounded-lg",
        "shrink-0",
        "transition-colors",
        "font-semibold",
        "select-none",
        "focus-visible:outline-none",
        "border",
        "border-transparent",
        "gap-2",
        "w-full",
    ],
    {
        variants: {
            orientation: {
                horizontal: ["justify-center"],
                vertical: ["justify-start"],
            },
            tabVariant: {
                "details-nav": [
                    "h-16",
                    "grow",
                    "py-3",
                    "px-1",
                    "border-t-3",
                    "border-x-0",
                    "border-b-0",
                    "rounded-none",
                    "translate-y-[1px]",
                    "[&>svg]:h-6",
                    "[&>svg]:w-6",
                ],
            },
            size: {
                sm: [
                    "h-[36px]",
                    "px-3",
                    "text-sm",
                    "gap-1.5",
                    "[&>svg]:h-4.5",
                    "[&>svg]:w-4.5",
                ],
                lg: [
                    "h-[44px]",
                    "px-4",
                    "text-sm",
                    "gap-2",
                    "[&>svg]:h-4.5",
                    "[&>svg]:w-4.5",
                ],
                xl: [
                    "h-[48px]",
                    "px-4.5",
                    "text-base",
                    "gap-2",
                    "[&>svg]:h-4.5",
                    "[&>svg]:w-4.5",
                ],
            },
        },
        compoundVariants: [
            {
                orientation: "horizontal",
                fullWidth: true,
                class: ["grow"],
            },
            {
                orientation: "vertical",
                fullWidth: true,
                class: ["grow"],
            },
            {
                active: true,
                class: "font-semibold",
            },
            // Button Primary
            {
                tabVariant: "button-primary",
                active: false,
                disabled: false,
                class: ["text-quaternary", "hover:border-secondary"],
            },
            {
                tabVariant: "button-primary",
                disabled: true,
                class: ["fg-quinary"],
            },
            {
                tabVariant: "button-primary",
                active: true,
                disabled: true,
                class: ["fg-quinary", "bg-disabled"],
            },
            {
                tabVariant: "button-primary",
                active: true,
                disabled: false,
                class: [
                    "bg-utility-brand-50_alt",
                    "fg-brand-primary_alt",
                    "hover:bg-utility-brand-100_alt",
                ],
            },
            {
                tabVariant: "button-primary",
                active: true,
                badge: true,
                disabled: false,
                class: [
                    "bg-utility-brand-50_alt",
                    "fg-brand-primary_alt",
                    "hover:bg-utility-brand-50_alt",
                    "[&>div]:border [&>div]:border-transparent",
                ],
            },
            {
                tabVariant: "button-primary",
                active: true,
                badge: true,
                disabled: true,
                class: ["fg-quinary", "bg-disabled"],
            },
            // Button Gray
            {
                tabVariant: "button-gray",
                disabled: false,
                class: ["text-quaternary", "hover:bg-primary_hover"],
            },
            {
                tabVariant: "button-gray",
                disabled: true,
                class: ["text-disabled"],
            },
            {
                tabVariant: "button-gray",
                active: true,
                class: ["bg-active", "text-secondary"],
            },
            {
                tabVariant: "button-gray",
                active: true,
                disabled: true,
                class: ["text-disabled", "bg-disabled"],
            },
            {
                tabVariant: "button-gray",
                active: true,
                badge: true,
                disabled: true,
                class: ["text-disabled", "bg-disabled"],
            },
            // Button White + Button White Border
            {
                tabVariant: ["button-white", "button-white-border"],
                disabled: false,
                class: [
                    "text-quaternary",
                    "hover:bg-primary_alt",
                    "hover:shadow-sm",
                    "hover:text-secondary",
                    "focus:shadow-sm",
                ],
            },
            {
                tabVariant: ["button-white", "button-white-border"],
                active: true,
                class: [
                    "bg-primary_alt",
                    "text-secondary",
                    "hover:bg-primary_alt",
                    "shadow-sm",
                ],
            },
            {
                tabVariant: ["button-white", "button-white-border"],
                disabled: true,
                class: ["text-disabled"],
            },
            // Underline
            {
                tabVariant: "underline",
                class: ["rounded-none"],
            },
            {
                tabVariant: "underline",
                active: true,
                disabled: false,
                class: ["fg-brand-primary_alt", "border-[#0683FF]"],
            },
            {
                tabVariant: "underline",
                active: false,
                disabled: false,
                class: ["hover:fg-brand-primary_alt", "hover:border-[#0683FF]"],
            },
            {
                tabVariant: "underline",
                disabled: true,
                class: ["text-disabled"],
            },
            {
                tabVariant: "underline",
                disabled: true,
                active: true,
                class: ["fg-disabled", "border-[#98A2B3]"],
            },
            {
                tabVariant: "underline",
                disabled: true,
                active: true,
                badge: true,
                class: ["fg-disabled", "border-[#98A2B3]"],
            },
            // Underline: Horizontal
            {
                tabVariant: "underline",
                size: "sm",
                orientation: "horizontal",
                class: [
                    "h-auto",
                    "px-1",
                    "pt-0",
                    "pb-2.5",
                    "border-t-0",
                    "border-x-0",
                    "border-b-[2px]",
                    "translate-y-[1px]",
                ],
            },
            {
                tabVariant: "underline",
                size: ["lg", "xl"],
                orientation: "horizontal",
                class: [
                    "h-auto",
                    "px-1",
                    "pt-3",
                    "pb-3.5",
                    "border-t-0",
                    "border-x-0",
                    "border-b-[2px]",
                    "translate-y-[1px]",
                ],
            },
            // Underline: Vertical
            {
                tabVariant: "underline",
                size: "sm",
                orientation: "vertical",
                class: [
                    "h-auto",
                    "py-2",
                    "pl-2.5",
                    "pr-3",
                    "border-t-0",
                    "border-y-0",
                    "border-l-[2px]",
                    "border-r-0",
                    "translate-x-[-1px]",
                ],
            },
            {
                tabVariant: "underline",
                size: ["lg", "xl"],
                orientation: "vertical",
                class: [
                    "h-auto",
                    "py-3",
                    "pl-3",
                    "pr-3.5",
                    "border-t-0",
                    "border-y-0",
                    "border-l-[2px]",
                    "border-r-0",
                    "translate-x-[-1px]",
                ],
            },
            // Underline: Task Details
            {
                variant: "taskDetails",
                tabVariant: "underline",
                class: [
                    "group-data-[collapsed=false]/tabs:pt-1",
                    "group-data-[collapsed=false]/tabs:pb-3",
                    "group-data-[collapsed=true]/tabs:fg-quaternary",
                    "group-data-[collapsed=true]/tabs:hover:fg-brand-primary_alt",
                    "group-data-[collapsed=true]/tabs:border-transparent",
                    "group-data-[collapsed=true]/tabs:hover:border-[#0683FF]",
                    "group-data-[collapsed=true]/tabs:w-12",
                    "group-data-[collapsed=true]/tabs:h-12",
                    "group-data-[collapsed=true]/tabs:justify-center",
                    "group-data-[collapsed=true]/tabs:py-3",
                    "group-data-[collapsed=true]/tabs:px-1",
                    "group-data-[collapsed=true]/tabs:[&>svg]:h-5",
                    "group-data-[collapsed=true]/tabs:[&>svg]:w-5",
                ],
            },
            // Underline Filled
            {
                tabVariant: "underline-filled",
                class: ["rounded-none"],
            },
            {
                tabVariant: "underline-filled",
                active: true,
                disabled: false,
                class: ["fg-brand-primary_alt", "bg-utility-brand-50_alt"],
            },
            {
                tabVariant: "underline-filled",
                active: false,
                disabled: false,
                class: [
                    "hover:fg-brand-primary_alt",
                    "hover:bg-utility-brand-50_alt",
                    "hover:border-[#0683FF]",
                ],
            },
            {
                tabVariant: "underline-filled",
                active: true,
                disabled: false,
                badge: true,
                class: ["[&>div]:border [&>div]:border-transparent"],
            },
            {
                tabVariant: "underline-filled",
                disabled: true,
                class: ["text-disabled"],
            },
            {
                tabVariant: "underline-filled",
                disabled: true,
                active: true,
                class: ["fg-disabled", "border-[#98A2B3]", "bg-disabled"],
            },
            {
                tabVariant: "underline-filled",
                disabled: true,
                active: true,
                badge: true,
                class: ["fg-disabled", "border-[#98A2B3]", "bg-disabled"],
            },
            // Underline Filled: Horizontal
            {
                tabVariant: "underline-filled",
                size: "sm",
                orientation: "horizontal",
                class: [
                    "h-auto",
                    "px-3",
                    "pt-2",
                    "pb-1.5",
                    "border-t-0",
                    "border-x-0",
                    "border-b-[2px]",
                    "translate-y-[1px]",
                ],
            },
            {
                tabVariant: "underline-filled",
                size: ["lg", "xl"],
                orientation: "horizontal",
                class: [
                    "h-auto",
                    "px-3",
                    "pt-3",
                    "pb-2.5",
                    "border-t-0",
                    "border-x-0",
                    "border-b-[2px]",
                    "translate-y-[1px]",
                ],
            },
            {
                tabVariant: "underline-filled",
                orientation: "horizontal",
                class: [
                    "border-t-0",
                    "border-x-0",
                    "border-b-[2px]",
                    "translate-y-[1px]",
                ],
            },
            // Underline Filled: Vertical
            {
                tabVariant: "underline-filled",
                size: "sm",
                orientation: "vertical",
                class: [
                    "h-auto",
                    "py-2",
                    "pl-2.5",
                    "pr-3",
                    "border-t-0",
                    "border-y-0",
                    "border-l-[2px]",
                    "border-r-0",
                    "translate-x-[-1px]",
                ],
            },
            {
                tabVariant: "underline-filled",
                size: ["lg", "xl"],
                orientation: "vertical",
                class: [
                    "h-auto",
                    "py-3",
                    "pl-3",
                    "pr-3.5",
                    "border-t-0",
                    "border-y-0",
                    "border-l-[2px]",
                    "border-r-0",
                    "translate-x-[-1px]",
                ],
            },
            {
                tabVariant: "underline-filled",
                orientation: "vertical",
                class: [
                    "border-t-0",
                    "border-y-0",
                    "border-l-[2px]",
                    "border-r-0",
                    "translate-x-[-1px]",
                ],
            },
            // Underline + Underline Filled
            {
                tabVariant: ["underline", "underline-filled"],
                disabled: false,
                class: ["text-quaternary", "rounded-none"],
            },
            {
                tabVariant: ["underline", "underline-filled"],
                active: true,
                disabled: false,
                class: ["border-[#0683FF]"],
            },
            // Details Nav
            {
                tabVariant: "details-nav",
                active: false,
                class: ["fg-quaternary", "border-transparent"],
            },
            {
                tabVariant: "details-nav",
                active: true,
                class: ["fg-brand-primary_alt", "border-brand-primary_alt"],
            },
        ],
        defaultVariants: {
            orientation: "horizontal",
            variant: "default",
            tabVariant: "button-primary",
            size: "md",
            fullWidth: false,
            active: false,
            disabled: false,
        },
    }
);

export const contentVariants = cva(
    [
        "flex",
        "flex-col",
        "bg-primary",
        "items-center",
        "justify-between",
        "grow",
    ],
    {
        variants: {
            variant: {
                notifications: [
                    "grow",
                    "bg-tertiary",
                    "rounded-b-xl",
                    "overflow-y-hidden",
                ],
                taskDetails: ["bg-primary", "overflow-y-scroll"],
                linkedToSelector: ["items-stretch"],
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);
